import { API_BASE_URL } from '../../constants/api';
import { io, Socket } from 'socket.io-client';

export interface WebSocketMessage {
  type: string;
  payload: any;
  timestamp: string;
}

export interface PermissionChangeEvent {
  type: 'PERMISSION_CHANGED' | 'ROLE_UPDATED' | 'MODULE_UPDATED';
  userId?: string;
  roleId?: string;
  moduleId?: string;
  permissionId?: string;
  action: 'ADDED' | 'REMOVED' | 'UPDATED';
  details: {
    roleName?: string;
    moduleName?: string;
    permissionName?: string;
    message: string;
  };
}

export interface NewContactMessageEvent {
  id: string;
  nombreCompleto: string;
  email: string;
  asunto: string;
  creadoEn: string;
}

export interface ChatMessageEvent {
  id: string;
  remitenteId: string;
  destinatarioId: string;
  nombreRemitente: string;
  contenido: string;
  creadoEn: string;
}

export type WebSocketEventHandler = (event: PermissionChangeEvent) => void;
export type NewContactMessageHandler = (event: NewContactMessageEvent) => void;
export type ChatMessageHandler = (event: ChatMessageEvent) => void;

class WebSocketService {
  private socket: Socket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectInterval = 3000;
  private isReconnecting = false;
  private eventHandlers: Map<string, WebSocketEventHandler[]> = new Map();
  private contactMessageHandlers: NewContactMessageHandler[] = [];
  private chatMessageHandlers: ChatMessageHandler[] = [];
  private connectionStatus: 'connected' | 'connecting' | 'disconnected' | 'error' = 'disconnected';
  private statusListeners: Array<(status: typeof this.connectionStatus) => void> = [];
  private connectPromise: Promise<void> | null = null;

  connect(token?: string): Promise<void> {
    // Si ya hay una conexión activa, retornar promise existente
    if (this.socket?.connected) {
      return Promise.resolve();
    }

    // Si ya hay una conexión en progreso, retornar la misma promise
    if (this.connectPromise) {
      return this.connectPromise;
    }

    this.connectPromise = new Promise((resolve, reject) => {
      try {
        const serverUrl = API_BASE_URL.replace('/api/v1', '');
        
        this.setConnectionStatus('connecting');
        this.socket = io(serverUrl + '/ws', {
          auth: {
            token: token
          },
          query: token ? { token } : {},
          transports: ['websocket', 'polling'],
          timeout: 10000,
          autoConnect: false
        });

        this.socket.on('connect', () => {
          this.reconnectAttempts = 0;
          this.isReconnecting = false;
          this.setConnectionStatus('connected');
          this.connectPromise = null;
          resolve();
        });

        this.socket.on('connection_established', () => {
        });

        this.socket.on('message', (message: WebSocketMessage) => {
          this.handleMessage(message);
        });

        this.socket.on('disconnect', () => {
          this.setConnectionStatus('disconnected');
          this.connectPromise = null;
          
          if (!this.isReconnecting && this.reconnectAttempts < this.maxReconnectAttempts) {
            this.isReconnecting = true;
            this.scheduleReconnect();
          } else if (this.reconnectAttempts >= this.maxReconnectAttempts) {
            this.setConnectionStatus('error');
          }
        });

        this.socket.on('connect_error', (error) => {
          console.error('🔌 Socket.IO error:', error);
          this.setConnectionStatus('error');
          this.connectPromise = null;
          reject(error);
        });

        this.socket.on('auth_error', (error) => {
          console.error('🔌 Authentication error:', error);
          this.setConnectionStatus('error');
          this.connectPromise = null;
          reject(error);
        });

        this.socket.connect();

      } catch (error) {
        console.error('🔌 Error creating Socket.IO:', error);
        this.setConnectionStatus('error');
        this.connectPromise = null;
        reject(error);
      }
    });

    return this.connectPromise;
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    this.connectPromise = null;
    this.reconnectAttempts = 0;
    this.isReconnecting = false;
    this.setConnectionStatus('disconnected');
  }

  private scheduleReconnect(): void {
    this.reconnectAttempts++;

    setTimeout(() => {
      const token = localStorage.getItem('accessToken');
      this.connect(token || undefined)
        .catch(() => {
          this.isReconnecting = false;
          if (this.reconnectAttempts < this.maxReconnectAttempts) {
            this.scheduleReconnect();
          }
        });
    }, this.reconnectInterval);
  }

  private handleMessage(message: WebSocketMessage): void {
    if (message.type === 'PERMISSION_CHANGE') {
      const event: PermissionChangeEvent = message.payload;
      this.notifyHandlers('permission_change', event);
    } else if (message.type === 'NEW_CONTACT_MESSAGE') {
      const event: NewContactMessageEvent = message.payload;
      this.notifyContactMessageHandlers(event);
    } else if (message.type === 'CHAT_MESSAGE') {
      const event: ChatMessageEvent = message.payload;
      this.notifyChatMessageHandlers(event);
    }
  }

  private notifyHandlers(eventType: string, event: PermissionChangeEvent): void {
    const handlers = this.eventHandlers.get(eventType) || [];
    handlers.forEach(handler => {
      try {
        handler(event);
      } catch (error) {
        console.error('Error in WebSocket event handler:', error);
      }
    });
  }

  private notifyContactMessageHandlers(event: NewContactMessageEvent): void {
    this.contactMessageHandlers.forEach(handler => {
      try {
        handler(event);
      } catch (error) {
        console.error('Error in contact message handler:', error);
      }
    });
  }

  private notifyChatMessageHandlers(event: ChatMessageEvent): void {
    this.chatMessageHandlers.forEach(handler => {
      try {
        handler(event);
      } catch (error) {
        console.error('Error in chat message handler:', error);
      }
    });
  }

  addEventListener(eventType: 'permission_change', handler: WebSocketEventHandler): void {
    const handlers = this.eventHandlers.get(eventType) || [];
    if (!handlers.includes(handler)) {
      handlers.push(handler);
      this.eventHandlers.set(eventType, handlers);
    }
  }

  removeEventListener(eventType: 'permission_change', handler: WebSocketEventHandler): void {
    const handlers = this.eventHandlers.get(eventType) || [];
    const index = handlers.indexOf(handler);
    if (index > -1) {
      handlers.splice(index, 1);
      this.eventHandlers.set(eventType, handlers);
    }
  }

  addContactMessageListener(handler: NewContactMessageHandler): void {
    if (!this.contactMessageHandlers.includes(handler)) {
      this.contactMessageHandlers.push(handler);
    }
  }

  removeContactMessageListener(handler: NewContactMessageHandler): void {
    const index = this.contactMessageHandlers.indexOf(handler);
    if (index > -1) {
      this.contactMessageHandlers.splice(index, 1);
    }
  }

  addChatMessageListener(handler: ChatMessageHandler): void {
    if (!this.chatMessageHandlers.includes(handler)) {
      this.chatMessageHandlers.push(handler);
    }
  }

  removeChatMessageListener(handler: ChatMessageHandler): void {
    const index = this.chatMessageHandlers.indexOf(handler);
    if (index > -1) {
      this.chatMessageHandlers.splice(index, 1);
    }
  }

  addStatusListener(listener: (status: typeof this.connectionStatus) => void): void {
    // Evitar duplicados
    if (!this.statusListeners.includes(listener)) {
      this.statusListeners.push(listener);
    }
    // Enviar el estado actual inmediatamente
    listener(this.connectionStatus);
  }

  removeStatusListener(listener: (status: typeof this.connectionStatus) => void): void {
    const index = this.statusListeners.indexOf(listener);
    if (index > -1) {
      this.statusListeners.splice(index, 1);
    }
  }

  private setConnectionStatus(status: typeof this.connectionStatus): void {
    this.connectionStatus = status;
    this.statusListeners.forEach(listener => {
      try {
        listener(status);
      } catch (error) {
        console.error('Error in status listener:', error);
      }
    });
  }

  getConnectionStatus(): typeof this.connectionStatus {
    return this.connectionStatus;
  }

  isConnected(): boolean {
    return this.socket?.connected || false;
  }

  send(message: any): void {
    if (this.isConnected()) {
      this.socket?.emit('message', message);
    } else {
      console.warn('🔌 No se puede enviar mensaje: Socket.IO no conectado');
    }
  }
}

// Instancia singleton
export const webSocketService = new WebSocketService();
